// Revisado: 2025-12-18 [I18n Scan]
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../../../constants';
import FadeImage from '../../../shared/FadeImage';
import { createPlaceholderUrl } from '../../../../utils/PlaceholderCreator';

// --- INLINE SVGs (Replaces lucide-react for Zero Dependencies) ---
const UsersIcon = ({ size = 24, strokeWidth = 2, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const SearchIcon = ({ size = 20, strokeWidth = 2, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
    </svg>
);

const XIcon = ({ size = 24, strokeWidth = 2, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
    </svg>
);

const GlobeIcon = ({ size = 24, strokeWidth = 2, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
        <path d="M2 12h20" />
    </svg>
);

const ArrowUpDownIcon = ({ size = 24, strokeWidth = 2, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="m21 16-4 4-4-4" />
        <path d="M17 20V4" />
        <path d="m3 8 4-4 4 4" />
        <path d="M7 4v16" />
    </svg>
);

const FilterIcon = ({ size = 24, strokeWidth = 2, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
);

const CheckIcon = ({ size = 24, strokeWidth = 2, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const ChevronDownIcon = ({ size = 24, strokeWidth = 2, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="m6 9 6 6 6-6" />
    </svg>
);

const CustomDropdown = ({ options, value, onChange }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const selectedOption = options.find(opt => opt.value === value);

    return (
        <div className="relative w-full">
            {/* Backdrop invisible para cerrar al hacer clic fuera */}
            {isOpen && (
                <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)}></div>
            )}

            {/* Trigger Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full p-3.5 px-4 rounded-2xl border bg-white dark:bg-slate-900/50 text-left flex items-center justify-between transition-all duration-200 outline-none
          ${isOpen
                        ? 'border-[#00c541] ring-2 ring-[#00c541]/20'
                        : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                    }
        `}
            >
                <span className="font-bold text-sm text-gray-700 dark:text-gray-200">
                    {selectedOption?.label || t('CharacterModal.select_default')}
                </span>
                <ChevronDownIcon
                    size={18}
                    className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#00c541]' : ''}`}
                />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-full bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200">
                    <div className="p-1.5 flex flex-col gap-1">
                        {options.map((option) => {
                            const isSelected = value === option.value;
                            return (
                                <button
                                    key={option.value}
                                    onClick={() => {
                                        onChange(option.value);
                                        setIsOpen(false);
                                    }}
                                    className={`
                    w-full text-left px-4 py-3 rounded-xl text-sm font-bold transition-colors flex items-center justify-between group
                    ${isSelected
                                            ? 'bg-[#00c541]/10 text-[#00c541]'
                                            : 'text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                                        }
                  `}
                                >
                                    {option.label}
                                    {isSelected && <CheckIcon size={16} className="text-[#00c541]" />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const CharacterModal = ({ characters, cards, isOpen, onClose, onCharacterSelect }) => {
    const { t } = useTranslation();

    // [LOGIC NOTE]: State Management
    // - searchTerm: Controlled input for filtering characters by name.
    // - sortBy: Controls sorting criteria ('name' or 'date').
    // - sortOrder: Controls sorting direction ('asc' or 'desc').
    // - region: Filter toggle for 'jp' (default) or 'en' (Global) release status.
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name'); // 'name' o 'date'
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' o 'desc'
    const [region, setRegion] = useState('jp'); // 'jp' o 'en'
    const [displayLimit, setDisplayLimit] = useState(36); // Progressive Loading

    // [LOGIC NOTE]: Effect - Reset Search & Limit
    // Ensures that when the modal opens, the search field is always empty.
    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setDisplayLimit(36);
        }
    }, [isOpen]);

    // Reset pagination when filters change
    useEffect(() => {
        setDisplayLimit(36);
    }, [searchTerm, region, sortBy, sortOrder]);

    const sanitizeFilename = (name) => {
        if (typeof name !== 'string') return '';
        return name.replace(/[\\/*?:"<>|]/g, '');
    };

    if (!isOpen) {
        return null;
    }

    // Función helper para obtener la fecha más temprana de un personaje
    const getEarliestDate = (charId) => {
        const charCards = cards.filter(c => c.Id_Personaje === charId);
        const validDates = charCards
            .map(c => c.release_ja)
            .filter(d => d)
            .map(d => new Date(d));
        return validDates.length > 0 ? new Date(Math.min(...validDates)) : null;
    };

    // [LOGIC NOTE]: Filtering Logic (Step 1)
    // - Filters by 'searchTerm' (case-insensitive).
    // - EXCLUDES 'NPC' type characters strictly.
    let filteredCharacters = characters.filter(char =>
        char.name_en.toLowerCase().includes(searchTerm.toLowerCase()) &&
        char.Player_type !== 'NPC'
    );

    // [LOGIC NOTE]: Filtering Logic (Step 2 - Region)
    // - If region is 'en', checks if the character has ANY card with a 'release_en' date.
    if (region === 'en') {
        filteredCharacters = filteredCharacters.filter(char => {
            const charCards = cards.filter(c => c.Id_Personaje === char.Id_Personaje);
            return charCards.some(card => card.release_en !== null);
        });
    }

    // [LOGIC NOTE]: Sorting Logic
    // - Sorts the filtered list based on 'sortBy' and 'sortOrder'.
    // - Uses 'getEarliestDate' helper for complex date sorting.
    const sortedCharacters = [...filteredCharacters].sort((a, b) => {
        let comparison = 0;

        if (sortBy === 'name') {
            comparison = a.name_en.localeCompare(b.name_en);
        } else if (sortBy === 'date') {
            const dateA = getEarliestDate(a.Id_Personaje);
            const dateB = getEarliestDate(b.Id_Personaje);
            if (!dateA && !dateB) comparison = 0;
            else if (!dateA) comparison = 1;
            else if (!dateB) comparison = -1;
            else comparison = dateA - dateB;
        }

        return sortOrder === 'asc' ? comparison : -comparison;
    });

    const sortOptions = [
        { value: 'name', label: t('CharacterModal.sort_name') },
        { value: 'date', label: t('CharacterModal.sort_date') }
    ];

    const handleSelect = (character) => {
        onCharacterSelect(character.Id_Personaje);
        onClose();
    };

    // Infinite Scroll Handler
    const handleScroll = (e) => {
        const { scrollTop, clientHeight, scrollHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 100) {
            if (displayLimit < sortedCharacters.length) {
                setDisplayLimit(prev => prev + 36);
            }
        }
    };

    const visibleCharacters = sortedCharacters.slice(0, displayLimit);

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
            {/* Container: Premium Rounded Card */}
            <div className="bg-white dark:bg-slate-800 w-full max-w-4xl h-[80vh] rounded-[2.5rem] shadow-2xl flex flex-col p-6 sm:p-8 border border-gray-100 dark:border-slate-700 transition-colors duration-300 overflow-hidden relative" onClick={(e) => e.stopPropagation()}>

                {/* Global Styles for Scrollbar */}
                <style>{`
          input[type="search"]::-webkit-search-decoration,
          input[type="search"]::-webkit-search-cancel-button,
          input[type="search"]::-webkit-search-results-button,
          input[type="search"]::-webkit-search-results-decoration {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar { width: 6px; }
          .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
        `}</style>

                {/* --- HEADER --- */}
                <div className="flex justify-between items-start mb-6 border-b border-gray-100 dark:border-slate-700/50 pb-4">
                    <div className="flex items-center gap-5">
                        <div className="hidden sm:flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#00c541] to-emerald-600 shadow-lg shadow-green-500/20 text-white transform -rotate-3 transition-transform hover:rotate-0">
                            <UsersIcon size={28} strokeWidth={2.5} />
                        </div>

                        <div className="flex flex-col">
                            <h2 className="text-3xl sm:text-4xl font-black text-gray-800 dark:text-white tracking-tighter uppercase italic leading-none">
                                {t('CharacterModal.title')}
                            </h2>
                            <div className="flex items-center gap-3 mt-1.5">
                                <div className="h-1.5 w-10 bg-[#00c541] rounded-full"></div>
                                <p className="text-[10px] sm:text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-[0.2em]">
                                    {t('CharacterModal.subtitle')}
                                </p>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="p-2.5 rounded-full bg-gray-50 dark:bg-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-400 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-400 transition-all active:scale-95"
                    >
                        <XIcon size={24} strokeWidth={2.5} />
                    </button>
                </div>

                {/* --- SEARCH & REGION ROW --- */}
                <div className="flex gap-3 mb-3">
                    <div className="flex-1 relative group">
                        <input
                            type="search"
                            placeholder={t('CharacterModal.placeholder_search')}
                            className="w-full p-3.5 pl-12 pr-12 rounded-2xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/50 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-[#00c541] focus:border-transparent outline-none transition-all placeholder-gray-400 font-bold"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />

                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#00c541] transition-colors">
                            <SearchIcon size={20} strokeWidth={2.5} />
                        </div>

                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-slate-600 hover:text-gray-700 dark:hover:text-gray-200 transition-all duration-200 shadow-sm"
                            >
                                <XIcon size={14} strokeWidth={3} />
                            </button>
                        )}
                    </div>

                    {/* Region Toggle */}
                    <button
                        onClick={() => setRegion(prev => prev === 'jp' ? 'en' : 'jp')}
                        className="px-5 py-2 rounded-2xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200 font-black text-sm whitespace-nowrap transition-all shadow-sm active:scale-95 flex items-center justify-center gap-2.5 min-w-[90px]"
                        title={region === 'jp' ? t('CharacterModal.tooltip_change_global') : t('CharacterModal.tooltip_change_jp')}
                    >
                        {region === 'jp' ? (
                            <>
                                <div className="w-5 h-3.5 bg-white border border-gray-200 flex items-center justify-center shadow-sm relative overflow-hidden rounded-[2px]">
                                    <div className="w-2.5 h-2.5 bg-red-600 rounded-full"></div>
                                </div>
                                <span>JP</span>
                            </>
                        ) : (
                            <>
                                <GlobeIcon size={18} className="text-blue-500 dark:text-blue-400" strokeWidth={2.5} />
                                <span>EN</span>
                            </>
                        )}
                    </button>
                </div>

                {/* --- SORT OPTIONS ROW --- */}
                <div className="flex gap-3 mb-6">
                    <div className="flex-1 relative z-30">
                        <CustomDropdown
                            options={sortOptions}
                            value={sortBy}
                            onChange={setSortBy}
                        />
                    </div>
                    <button
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        className="px-4 py-2 rounded-2xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400 active:bg-gray-100 transition-colors shadow-sm flex items-center justify-center"
                        title={t('CharacterModal.tooltip_change_order')}
                    >
                        <ArrowUpDownIcon size={20} strokeWidth={2} className={sortOrder === 'desc' ? 'rotate-180 transition-transform' : 'transition-transform'} />
                    </button>
                </div>

                {/* --- GRID CONTAINER --- */}
                <div
                    className="flex-1 overflow-y-auto p-4 bg-gray-50/50 dark:bg-slate-900/30 rounded-3xl border border-gray-100 dark:border-slate-700/30 relative custom-scrollbar z-0"
                    onScroll={handleScroll}
                >
                    <div className="flex flex-wrap justify-center content-start gap-4 sm:gap-6 pt-2">
                        {visibleCharacters.map(char => {
                            // [LOGIC NOTE]: Image URL Generation (Optimized)
                            // 1. Definition of default placeholder using API fallback if needed or static
                            // 1. Definition of default placeholder using API fallback if needed or static
                            let finalUrl = createPlaceholderUrl(100, 100, char.name_en.substring(0, 2));

                            // 2. Search for the 'Original' costume in the 'cards' prop.
                            let originalCostume = null;
                            if (cards && cards.length > 0) {
                                originalCostume = cards.find(c =>
                                    c.Id_Personaje === char.Id_Personaje && c.reference === 'Original'
                                );
                            }

                            // 3. Construction of the specific file path based on sanitized names.
                            if (originalCostume) {
                                try {
                                    const characterName = sanitizeFilename(char.name_en).replace(/ /g, '_');
                                    const reference = sanitizeFilename(originalCostume.reference).replace(/ /g, '_');
                                    const costumeName = sanitizeFilename(originalCostume.title_en).replace(/ /g, '_');

                                    // Standard format: Character_Ref_Costume.png
                                    const imageName = `${characterName}_${reference}_${costumeName}.png`;
                                    const originalPath = `/images/character/${imageName}`;

                                    // Use API with whitelist resolution (128px)
                                    finalUrl = `${API_BASE_URL}/api/thumb?img=${originalPath}&width=128`;
                                } catch (error) {
                                    console.error("Could not construct thumbnail URL for:", char.name_en, error);
                                }
                            }

                            return (
                                <button
                                    key={char.Id_Personaje}
                                    onClick={() => handleSelect(char)}
                                    // [VISUAL NOTE]: Grid Item - Card Container
                                    className="group relative flex flex-col items-center justify-between w-36 sm:w-44 p-3 rounded-2xl transition-all duration-300 cursor-pointer bg-white dark:bg-slate-800/60 border-2 border-gray-100 dark:border-slate-700/50 hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-md hover:-translate-y-1"
                                >
                                    {/* [VISUAL NOTE]: Image Container - Circular with Hover Ring */}
                                    <div className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-full p-1 mb-3 transition-all duration-300 group-hover:ring-2 group-hover:ring-gray-200 dark:group-hover:ring-slate-600">
                                        <div className="absolute inset-0 bg-gray-100 dark:bg-slate-700 rounded-full"></div>
                                        <FadeImage
                                            src={finalUrl}
                                            alt={char.name_en}
                                            className="w-full h-full rounded-full bg-white dark:bg-slate-800 border-2 border-white dark:border-slate-800 relative z-10"
                                            loading="lazy"
                                        />
                                    </div>

                                    {/* [VISUAL NOTE]: Name Badge - Bottom Label */}
                                    <div className="w-full px-2 py-1 rounded-lg text-center transition-colors duration-300 bg-transparent">
                                        <p className={`text-[10px] sm:text-xs font-bold uppercase tracking-wide leading-tight line-clamp-2 text-gray-500 dark:text-slate-400 group-hover:text-gray-800 dark:group-hover:text-slate-200 ${['Matikanefukukitaru', 'Matikanetannhauser'].includes(char.name_en) ? 'text-[9px]' : ''}`}>
                                            {char.name_en}
                                        </p>
                                    </div>
                                </button>
                            );
                        })}


                        {/* --- STATE: Empty --- */}
                        {sortedCharacters.length === 0 && (
                            <div className="w-full py-20 flex flex-col items-center justify-center text-gray-400 dark:text-slate-500 animate-in fade-in zoom-in-95 duration-300">
                                <div className="p-4 bg-gray-100 dark:bg-slate-800 rounded-full mb-3 ring-1 ring-gray-200 dark:ring-slate-700">
                                    <FilterIcon size={32} className="opacity-40" />
                                </div>
                                <p className="font-bold text-sm mb-2">{t('CharacterModal.empty_msg')}</p>
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="text-xs text-[#00c541] hover:text-[#00a035] hover:underline font-bold transition-colors"
                                >
                                    {t('CharacterModal.button_clear')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CharacterModal;
// Revisado: 2025-12-16
